import moment from 'moment';
import { MemberProfileService } from '@/modules/amlpro/services/member-profile-service';

const memberSvc = new MemberProfileService();

const CLOSED_STATUSES = [
  'CLOSED_INVESTIGATION_COMPLETED',
  'CLOSED_NO_ACTION',
  'CLOSED_REPORT_FILED',
];

export default {
  methods: {

    /**
     * Returns a formatted full name from a User object
     * (which has an optional nested `person` record).
     * Falls back to username, then '—'.
     */
    assigneeName(user) {
      if (!user) return '—';
      const full = (`${user.person?.firstName ?? ''} ${user.person?.lastName ?? ''}`).trim()
        || user.username
        || '';
      return full ? this.capitalizeName(full) : '—';
    },

    /**
     * Two-letter initials from a User object (for avatars in comment threads).
     */
    userInitials(user) {
      if (!user) return '?';
      const first = user.person?.firstName?.[0] ?? '';
      const last  = user.person?.lastName?.[0]  ?? '';
      return (first + last).toUpperCase() || (user.username?.[0] ?? '?').toUpperCase();
    },

    /**
     * Two-letter initials from a plain Person object (account-holder avatars).
     */
    personInitials(person) {
      if (!person) return '?';
      const first = (person.firstName?.[0] ?? '').toUpperCase();
      const last  = (person.lastName?.[0]  ?? '').toUpperCase();
      return first + last || '?';
    },

    // Date helpers

    /** "Jan 5, 2025" */
    fmtDate(d) {
      return d ? moment(d).format('MMM D, YYYY') : '—';
    },

    /** "3 hours ago" */
    timeAgo(d) {
      return d ? moment(d).fromNow() : '—';
    },

    // Case status

    isClosedStatus(s) {
      return CLOSED_STATUSES.includes(s);
    },

    /**
     * Converts a raw status enum value to a readable label.
     * e.g. "CLOSED_NO_ACTION" → "No Action Taken"
     */
    formatStatus(s) {
      if (!s) return s;
      const friendly = {
        OPEN:                           'Open',
        CLOSED_INVESTIGATION_COMPLETED: 'Investigation Completed',
        CLOSED_NO_ACTION:               'No Action Taken',
        CLOSED_REPORT_FILED:            'Report Filed',
      };
      return (
        friendly[s] ??
        s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      );
    },

    /** Chip colour for a case status value. */
    statusColor(s) {
      const map = {
        OPEN:                           'blue',
        CLOSED_INVESTIGATION_COMPLETED: 'green',
        CLOSED_NO_ACTION:               'grey darken-1',
        CLOSED_REPORT_FILED:            'purple',
      };
      return map[s] ?? 'grey';
    },

    // Case priority

    /** Chip colour for a case priority value. */
    priorityColor(p) {
      const map = { HIGH: 'red', MEDIUM: 'orange', LOW: 'green' };
      return map[p] ?? 'grey';
    },

    // Flagged-attempt pass-through type

    /** Chip colour for a passThroughType value. */
    typeColor(t) {
      const map = {
        ACCOUNT_HOLDER:     'blue',
        NON_ACCOUNT_HOLDER: 'teal',
        SENDER:             'orange',
        RECEIVER:           'purple',
      };
      return map[t] ?? 'grey';
    },

    /** Human-readable label for a passThroughType value. */
    formatType(t) {
      const map = {
        ACCOUNT_HOLDER:     'Account Holder',
        NON_ACCOUNT_HOLDER: 'Non-Account Holder',
        SENDER:             'Sender',
        RECEIVER:           'Receiver',
      };
      return map[t] ?? (t?.replace(/_/g, ' ') ?? t);
    },

    /**
     * Searches members by name or TRN and flattens the result into a simple
     * { accountId, mainAccountNumber, personName, trn, person } list.
     *
     * Reads:  this.accountSearch.query
     * Writes: this.accountSearch.results, this.accountSearch.searched
     *         this.searchingAccounts
     */
    async searchAccounts() {
      if (!this.accountSearch.query.trim()) return;
      this.searchingAccounts      = true;
      this.accountSearch.results  = [];
      this.accountSearch.searched = false;
      try {
        const q       = this.accountSearch.query.trim();
        const filters = q.match(/^\d/) ? { trn: q } : { firstName: q };
        const res     = await memberSvc.searchMembers(filters, 0, 20);
        const data    = res.data ?? res;
        const persons = data.data ?? data;
        const results = [];

        for (const p of persons) {
          const accts = p.accounts ?? [];
          if (accts.length) {
            for (const pta of accts) {
              const a = pta.account ?? pta;
              results.push({
                accountId:         a.id,
                mainAccountNumber: a.mainAccountNumber,
                personName:        `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
                trn:               p.trn ?? '',
                person:            p,
              });
            }
          } else {
            results.push({
              accountId:         null,
              mainAccountNumber: '—',
              personName:        `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
              trn:               p.trn ?? '',
              person:            p,
            });
          }
        }

        this.accountSearch.results  = results;
        this.accountSearch.searched = true;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Search failed' });
      } finally {
        this.searchingAccounts = false;
      }
    },
  },
};